import { useState } from "react";
import { useLocation } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  GardenCameraCapture,
  type CapturedPhoto,
} from "@/components/GardenCameraCapture";
import { parseErrorMessage } from "@/lib/gardenFetch";
import { useToast } from "@/hooks/use-toast";

type Mode = "identify" | "diagnose";

interface Diagnosis {
  category: string;
  confidence: string;
  findings: string[];
  remediation: string[];
  rawNotes?: string;
}

type LifecycleCategory =
  | "ornamental"
  | "native"
  | "edible"
  | "houseplant"
  | "weed"
  | "invasive"
  | "unknown";

interface Identification {
  commonName: string;
  latinName: string | null;
  catalogSlug: string | null;
  confidence: "high" | "medium" | "low";
  lifecycleCategory?: LifecycleCategory;
  description?: string | null;
  bloomTime?: string | null;
  bloomDescription?: string | null;
  distinguishingFeatures: string[];
  careTips: string[];
  alternateGuesses: string[];
}

function lifecycleLabel(category: LifecycleCategory): string {
  switch (category) {
    case "ornamental": return "Ornamental";
    case "native": return "Native wildflower";
    case "edible": return "Edible";
    case "houseplant": return "Houseplant";
    case "weed": return "Weed";
    case "invasive": return "Invasive";
    default: return "";
  }
}

function exampleImagesUrl(latinName: string | null, commonName: string): string {
  const q = (latinName && latinName.trim()) || commonName;
  return `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(q + " plant")}`;
}

interface IdentifyResponse {
  identification: Identification;
  entryId: string;
}

interface GardenState {
  garden: { id: string } | null;
  plants: Array<{ id: string; plantSlug: string }>;
}

interface CatalogEntry {
  slug: string;
  commonName: string;
}

export default function GardenDiagnose() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [mode, setMode] = useState<Mode>("identify");
  const [photo, setPhoto] = useState<CapturedPhoto | null>(null);
  const [description, setDescription] = useState("");
  const [gardenPlantId, setGardenPlantId] = useState<string>("");
  const [diagnosis, setDiagnosis] = useState<Diagnosis | null>(null);
  const [identification, setIdentification] = useState<Identification | null>(null);
  const [entryId, setEntryId] = useState<string | null>(null);

  const { data: state } = useQuery<GardenState>({
    queryKey: ["/api/garden"],
    queryFn: async () => {
      const res = await fetch("/api/garden", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: mode === "diagnose",
  });

  const { data: catalogData } = useQuery<{ catalog: CatalogEntry[] }>({
    queryKey: ["/api/garden/catalog"],
    queryFn: async () => {
      const res = await fetch("/api/garden/catalog", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: mode === "diagnose",
  });

  const diagnoseMutation = useMutation({
    mutationFn: async () => {
      if (!photo) throw new Error("Upload a photo first");
      const res = await fetch("/api/garden/issues", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          photoDataUrl: photo.dataUrl,
          userDescription: description,
          gardenPlantId: gardenPlantId || undefined,
        }),
      });
      if (!res.ok) throw new Error(await parseErrorMessage(res, "Diagnosis failed"));
      return res.json();
    },
    onSuccess: (data) => {
      setDiagnosis(data.issue?.diagnosisJson ?? null);
    },
  });

  const identifyMutation = useMutation<IdentifyResponse>({
    mutationFn: async () => {
      if (!photo) throw new Error("Take a photo first");
      const res = await fetch("/api/garden/identify", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ photoDataUrl: photo.dataUrl }),
      });
      if (!res.ok) throw new Error(await parseErrorMessage(res, "Identification failed"));
      return res.json();
    },
    onSuccess: (data) => {
      setIdentification(data.identification ?? null);
      setEntryId(data.entryId ?? null);
    },
  });

  const pending =
    mode === "diagnose" ? diagnoseMutation.isPending : identifyMutation.isPending;
  const error = mode === "diagnose" ? diagnoseMutation.error : identifyMutation.error;
  const ctaLabel = mode === "diagnose"
    ? pending ? "Diagnosing…" : "Diagnose"
    : pending ? "Identifying…" : "Identify";

  const submit = () => {
    if (!photo) {
      toast({
        title: "Add a photo first",
        description:
          "Tap the photo button at the top to take or upload a close-up of the plant.",
      });
      return;
    }
    if (mode === "diagnose") diagnoseMutation.mutate();
    else identifyMutation.mutate();
  };

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4 pb-24">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">
          {mode === "diagnose" ? "Diagnose a plant issue" : "Identify a plant"}
        </h1>
        <Button variant="outline" size="sm" onClick={() => setLocation("/garden")}>
          Back
        </Button>
      </div>

      <ToggleGroup
        type="single"
        value={mode}
        onValueChange={(v) => {
          if (v !== "identify" && v !== "diagnose") return;
          setMode(v);
        }}
        className="w-full justify-stretch rounded-lg border bg-muted/30 p-1"
      >
        <ToggleGroupItem value="identify" className="flex-1">
          What is it?
        </ToggleGroupItem>
        <ToggleGroupItem value="diagnose" className="flex-1">
          What's wrong?
        </ToggleGroupItem>
      </ToggleGroup>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {mode === "diagnose" ? "Upload a close-up photo" : "Photo of the plant"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <GardenCameraCapture onCaptured={(p) => setPhoto(p)} />
          {photo ? (
            <img
              src={photo.dataUrl}
              alt={mode === "diagnose" ? "Plant issue" : "Plant to identify"}
              className="max-h-64 w-full rounded border object-contain"
            />
          ) : (
            <p className="text-xs text-muted-foreground">
              {mode === "diagnose"
                ? "Use the button above to take or pick a photo. Diagnosis won't run without one."
                : "A close-up of leaves, flowers, or fruit gives the best result."}
            </p>
          )}
        </CardContent>
      </Card>

      {mode === "diagnose" ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Which plant? (optional)</CardTitle>
            </CardHeader>
            <CardContent>
              <select
                className="w-full rounded border bg-background p-2 text-sm"
                value={gardenPlantId}
                onChange={(e) => setGardenPlantId(e.target.value)}
              >
                <option value="">(Not sure)</option>
                {state?.plants?.map((p) => {
                  const c = catalogData?.catalog.find((x) => x.slug === p.plantSlug);
                  return (
                    <option key={p.id} value={p.id}>
                      {c?.commonName ?? p.plantSlug}
                    </option>
                  );
                })}
              </select>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">What do you see? (optional)</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Yellow leaves, holes in the fruit, watered 3x/week..."
              />
            </CardContent>
          </Card>
        </>
      ) : null}

      <Button disabled={pending} onClick={submit}>
        {ctaLabel}
      </Button>

      {error ? <div className="text-sm text-destructive">{String(error)}</div> : null}

      {mode === "diagnose" && diagnosis ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between text-base">
              <span>Diagnosis</span>
              <div className="flex gap-2">
                <Badge variant="outline">{diagnosis.category}</Badge>
                <Badge variant="secondary">{diagnosis.confidence} confidence</Badge>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {diagnosis.findings.length ? (
              <div>
                <div className="mb-1 text-sm font-medium">What we see</div>
                <ul className="list-disc pl-5 text-sm">
                  {diagnosis.findings.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {diagnosis.remediation.length ? (
              <div>
                <div className="mb-1 text-sm font-medium">Fix it this week</div>
                <ol className="list-decimal pl-5 text-sm">
                  {diagnosis.remediation.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ol>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {mode === "identify" && identification ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between text-base">
              <span>{identification.commonName}</span>
              <Badge variant="secondary">{identification.confidence} confidence</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {identification.latinName ? (
              <div className="text-sm italic text-muted-foreground">
                {identification.latinName}
              </div>
            ) : null}

            {identification.lifecycleCategory && identification.lifecycleCategory !== "unknown" ? (
              <Badge variant="outline">{lifecycleLabel(identification.lifecycleCategory)}</Badge>
            ) : null}

            {identification.description ? (
              <p className="text-sm text-muted-foreground">{identification.description}</p>
            ) : null}

            {identification.bloomTime || identification.bloomDescription ? (
              <div>
                <div className="mb-1 text-sm font-medium">Blooms</div>
                <p className="text-sm">
                  {[identification.bloomTime, identification.bloomDescription].filter(Boolean).join(" — ")}
                </p>
              </div>
            ) : null}

            {identification.distinguishingFeatures.length ? (
              <div>
                <div className="mb-1 text-sm font-medium">What we see</div>
                <ul className="list-disc pl-5 text-sm">
                  {identification.distinguishingFeatures.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {identification.careTips.length ? (
              <div>
                <div className="mb-1 text-sm font-medium">Care tips</div>
                <ul className="list-disc pl-5 text-sm">
                  {identification.careTips.map((t, i) => (
                    <li key={i}>{t}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {identification.alternateGuesses.length ? (
              <div>
                <div className="mb-1 text-sm font-medium">Could also be</div>
                <div className="flex flex-wrap gap-2">
                  {identification.alternateGuesses.map((g, i) => (
                    <Badge key={i} variant="outline">
                      {g}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : null}

            <a
              href={exampleImagesUrl(identification.latinName, identification.commonName)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-sm text-primary underline"
            >
              See more example photos
            </a>

            {entryId ? (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setLocation(`/garden/journal/${entryId}`)}
              >
                Open in journal
              </Button>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
